@if (isEmpty()) {
  <div class="report-empty">
    <app-empty-state
      variant="inline"
      icon="draft"
      [title]="'panel_assessment_info.dpia_report.empty_title' | transloco"
      [message]="'panel_assessment_info.dpia_report.empty_message' | transloco"
    />
  </div>
} @else {
  <div class="report">
    <!-- ── Risk snapshot ───────────────────────────────────────
         Two cells (overall → residual) framed as a
         "before / after" pair so the reviewer's eye lands on the
         delta between the raw and residual grades. Arrow between
         cells reinforces the temporal direction. -->
    @if (hasRiskData()) {
      <section class="report__section">
        <h3 class="report__kicker">
          {{ 'panel_assessment_info.dpia_report.section_snapshot' | transloco }}
        </h3>
        <div class="report__snapshot">
          <div class="report__snapshot-cell">
            <span class="report__snapshot-label">
              {{ 'assessment.risk.dpia_overall_prefix' | transloco }}
            </span>
            @if (overallRisk(); as r) {
              <app-dpia-risk-badge [risk]="r" />
            } @else {
              <span class="report__snapshot-empty">—</span>
            }
          </div>
          <mat-icon class="report__snapshot-arrow" aria-hidden="true">arrow_forward</mat-icon>
          <div class="report__snapshot-cell">
            <span class="report__snapshot-label">
              {{ 'assessment.risk.dpia_residual_prefix' | transloco }}
            </span>
            @if (residualRisk(); as r) {
              <app-dpia-risk-badge [risk]="r" />
            } @else {
              <span class="report__snapshot-empty">—</span>
            }
          </div>
        </div>
      </section>
    }

    <!-- ── Prior consultation callout ────────────────────────── -->
    @if (needsPriorConsultation()) {
      <div class="report__callout" role="note">
        <mat-icon class="report__callout-icon" aria-hidden="true">gavel</mat-icon>
        <div>
          <div class="report__callout-title">
            {{ 'panel_assessment_info.dpia_report.prior_consultation_title' | transloco }}
          </div>
          <p class="report__callout-body">
            {{ 'panel_assessment_info.dpia_report.prior_consultation_body' | transloco }}
          </p>
        </div>
      </div>
    }

    <!-- ── Risks list ────────────────────────────────────────── -->
    <section class="report__section">
      <div class="report__header">
        <h3 class="report__kicker">
          {{ 'panel_assessment_info.dpia_report.section_risks' | transloco }}
        </h3>
        @if (hasRisks()) {
          <span class="report__count">{{ riskRows().length }}</span>
        }
      </div>
      @if (hasRisks()) {
        <ul class="report__list" role="list">
          @for (row of riskRows(); track row.id) {
            <li class="report__risk">
              <div class="report__risk-head">
                <span class="report__risk-title" [title]="row.title">{{ row.title }}</span>
                @if (row.hasUnresolvedLinks) {
                  <mat-icon
                    class="report__risk-warn"
                    aria-hidden="true"
                    [matTooltip]="
                      'panel_assessment_info.dpia_report.row_unresolved_tooltip' | transloco
                    "
                    >error</mat-icon
                  >
                }
              </div>
              <div class="report__risk-meta">
                <span class="report__risk-ls">
                  {{ row.likelihoodKey | transloco }}
                  <span class="report__meta-dot" aria-hidden="true">·</span>
                  {{ row.severityKey | transloco }}
                </span>
                <div class="report__risk-badges">
                  @if (row.overall) {
                    <app-dpia-risk-badge [risk]="row.overall" />
                  }
                  @if (row.residual) {
                    <mat-icon class="report__risk-badge-arrow" aria-hidden="true"
                      >arrow_forward</mat-icon
                    >
                    <app-dpia-risk-badge [risk]="row.residual" />
                  }
                </div>
                <span class="report__risk-measures">
                  {{
                    'panel_assessment_info.dpia_report.row_measures'
                      | transloco: { count: row.measuresCount }
                  }}
                </span>
              </div>
            </li>
          }
        </ul>
      } @else {
        <p class="report__inline-empty">
          {{ 'panel_assessment_info.dpia_report.risks_empty' | transloco }}
        </p>
      }
    </section>

    <!-- ── Sign-off ledger ───────────────────────────────────── -->
    <section class="report__section">
      <div class="report__header">
        <h3 class="report__kicker">
          {{ 'panel_assessment_info.dpia_report.section_sign_off' | transloco }}
        </h3>
        <span class="report__progress">
          {{
            'dpia.sign_off.progress'
              | transloco: { signed: signOffProgress().signed, total: signOffProgress().total }
          }}
        </span>
      </div>
      <div class="report__progress-track" aria-hidden="true">
        <div
          class="report__progress-fill"
          [style.width.%]="(signOffProgress().signed / signOffProgress().total) * 100"
        ></div>
      </div>
      <ul class="report__signoff-list" role="list">
        @for (slot of signOffSlots(); track slot.role) {
          <li class="report__signoff" [class.report__signoff--empty]="!slot.signed">
            <div class="report__signoff-role">{{ slot.roleLabelKey | transloco }}</div>
            @if (slot.signed; as s) {
              <div class="report__signoff-body">
                <div class="report__signoff-name">{{ s.name }}</div>
                <div class="report__signoff-meta">
                  <span>{{ s.signed_at | date: 'mediumDate' }}</span>
                  @if (s.comment) {
                    <span class="report__meta-dot" aria-hidden="true">·</span>
                    <span class="report__signoff-comment" [matTooltip]="s.comment">
                      {{ s.comment }}
                    </span>
                  }
                </div>
              </div>
              <span
                class="report__signoff-decision"
                [class]="'report__signoff-decision--' + slot.decisionVariant!"
              >
                {{ slot.decisionLabelKey! | transloco }}
              </span>
            } @else {
              <div class="report__signoff-awaiting">
                {{ 'dpia.sign_off.awaiting' | transloco }}
              </div>
            }
          </li>
        }
      </ul>
    </section>
  </div>
}
